import { useEffect, useRef, useCallback } from "react";

/**
 * CursorEffect — renders an interactive canvas overlay behind section content.
 * Types: "particles" | "spotlight" | "trail"
 * Must be placed inside a parent with position: relative and overflow: hidden.
 */
const CursorEffect = ({ type = "particles" }) => {
  const canvasRef = useRef(null);
  const animRef = useRef(null);
  const mouseRef = useRef({ x: -200, y: -200, active: false });
  const dataRef = useRef(null);

  const initParticles = useCallback((w, h) => {
    const count = Math.min(Math.floor((w * h) / 8000), 90);
    return Array.from({ length: count }, () => {
      const x = Math.random() * w;
      const y = Math.random() * h;
      return { x, y, ox: x, oy: y, vx: 0, vy: 0, size: Math.random() * 2.2 + 0.8 };
    });
  }, []);

  const initTrailPoints = useCallback(() => [], []);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    let visible = false;

    const resize = () => {
      const parent = canvas.parentElement;
      if (!parent) return;
      const { width, height } = parent.getBoundingClientRect();
      canvas.width = width;
      canvas.height = height;
      if (type === "particles") dataRef.current = initParticles(width, height);
      if (type === "trail") dataRef.current = initTrailPoints();
    };

    resize();

    // IntersectionObserver — only animate when visible
    const observer = new IntersectionObserver(
      ([entry]) => { visible = entry.isIntersecting; },
      { threshold: 0.05 }
    );
    observer.observe(canvas);

    // Mouse tracking relative to canvas
    const onMove = (e) => {
      const rect = canvas.getBoundingClientRect();
      mouseRef.current.x = e.clientX - rect.left;
      mouseRef.current.y = e.clientY - rect.top;
      mouseRef.current.active = true;

      if (type === "trail" && visible) {
        if (!dataRef.current) dataRef.current = [];
        dataRef.current.push({
          x: mouseRef.current.x,
          y: mouseRef.current.y,
          life: 1,
        });
        if (dataRef.current.length > 45) dataRef.current.shift();
      }
    };

    const onLeave = () => { mouseRef.current.active = false; };

    const parentEl = canvas.parentElement;
    parentEl.addEventListener("mousemove", onMove, { passive: true });
    parentEl.addEventListener("mouseleave", onLeave, { passive: true });

    // --- Animation loops per type ---
    const animateParticles = () => {
      animRef.current = requestAnimationFrame(animateParticles);
      if (!visible) return;
      const particles = dataRef.current;
      if (!particles) return;
      const { width: w, height: h } = canvas;
      ctx.clearRect(0, 0, w, h);
      const mx = mouseRef.current.x;
      const my = mouseRef.current.y;
      const isActive = mouseRef.current.active;

      for (let i = 0; i < particles.length; i++) {
        const p = particles[i];
        if (isActive) {
          const dx = mx - p.x;
          const dy = my - p.y;
          const dist = Math.sqrt(dx * dx + dy * dy);
          if (dist < 150) {
            const force = (1 - dist / 150) * 0.04;
            p.vx -= dx * force;
            p.vy -= dy * force;
          }
        }
        // Spring back to origin
        p.vx += (p.ox - p.x) * 0.015;
        p.vy += (p.oy - p.y) * 0.015;
        p.vx *= 0.93;
        p.vy *= 0.93;
        p.x += p.vx;
        p.y += p.vy;

        const dist = isActive ? Math.sqrt((mx - p.x) ** 2 + (my - p.y) ** 2) : 999;
        const alpha = dist < 150 ? 0.7 + (1 - dist / 150) * 0.3 : 0.25;
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(129,140,248,${alpha})`;
        ctx.fill();

        // Draw connection lines between nearby particles near cursor
        if (isActive && dist < 180) {
          for (let j = i + 1; j < particles.length; j++) {
            const p2 = particles[j];
            const d2 = Math.sqrt((mx - p2.x) ** 2 + (my - p2.y) ** 2);
            if (d2 < 180) {
              const dd = Math.sqrt((p.x - p2.x) ** 2 + (p.y - p2.y) ** 2);
              if (dd < 100) {
                ctx.beginPath();
                ctx.moveTo(p.x, p.y);
                ctx.lineTo(p2.x, p2.y);
                ctx.strokeStyle = `rgba(129,140,248,${(1 - dd / 100) * 0.15})`;
                ctx.lineWidth = 0.6;
                ctx.stroke();
              }
            }
          }
        }
      }
    };

    const animateTrail = () => {
      animRef.current = requestAnimationFrame(animateTrail);
      if (!visible) return;
      const { width: w, height: h } = canvas;
      ctx.clearRect(0, 0, w, h);
      const points = dataRef.current;
      if (!points) return;

      for (let i = points.length - 1; i >= 0; i--) {
        const p = points[i];
        p.life -= 0.012;
        if (p.life <= 0) { points.splice(i, 1); continue; }
        const ease = p.life * p.life; // quadratic ease-out for smoother fade
        const radius = ease * 18;
        const gradient = ctx.createRadialGradient(p.x, p.y, 0, p.x, p.y, radius);
        gradient.addColorStop(0, `rgba(132, 72, 252,${ease * 0.4})`);
        gradient.addColorStop(0.6, `rgba(129,140,248,${ease * 0.15})`);
        gradient.addColorStop(1, "transparent");
        ctx.beginPath();
        ctx.arc(p.x, p.y, radius, 0, Math.PI * 2);
        ctx.fillStyle = gradient;
        ctx.fill();
      }
    };

    const animateSpotlight = () => {
      animRef.current = requestAnimationFrame(animateSpotlight);
      if (!visible) return;
      const { width: w, height: h } = canvas;
      ctx.clearRect(0, 0, w, h);
      if (!mouseRef.current.active) return;
      const mx = mouseRef.current.x;
      const my = mouseRef.current.y;

      // Outer soft glow
      const outerGrad = ctx.createRadialGradient(mx, my, 0, mx, my, 220);
      outerGrad.addColorStop(0, "rgba(132, 72, 252,0.12)");
      outerGrad.addColorStop(0.5, "rgba(132, 72, 252,0.04)");
      outerGrad.addColorStop(1, "transparent");
      ctx.fillStyle = outerGrad;
      ctx.fillRect(0, 0, w, h);

      // Inner bright core
      const innerGrad = ctx.createRadialGradient(mx, my, 0, mx, my, 80);
      innerGrad.addColorStop(0, "rgba(129,140,248,0.15)");
      innerGrad.addColorStop(1, "transparent");
      ctx.fillStyle = innerGrad;
      ctx.fillRect(0, 0, w, h);
    };

    if (type === "particles") animateParticles();
    else if (type === "trail") animateTrail();
    else if (type === "spotlight") animateSpotlight();

    const resizeObserver = new ResizeObserver(resize);
    resizeObserver.observe(canvas.parentElement);

    return () => {
      if (animRef.current) cancelAnimationFrame(animRef.current);
      observer.disconnect();
      resizeObserver.disconnect();
      parentEl.removeEventListener("mousemove", onMove);
      parentEl.removeEventListener("mouseleave", onLeave);
    };
  }, [type, initParticles, initTrailPoints]);

  // Don't render on touch-only devices
  if (typeof window !== "undefined" && "ontouchstart" in window && !window.matchMedia("(pointer: fine)").matches) {
    return null;
  }

  return (
    <canvas
      ref={canvasRef}
      data-testid={`cursor-effect-${type}`}
      style={{
        position: "absolute",
        inset: 0,
        width: "100%",
        height: "100%",
        pointerEvents: "none",
        zIndex: 0,
      }}
    />
  );
};

export default CursorEffect;
